import { useState } from 'react';

import { api } from '../api';
import type { PortalConfig } from '../config';
import type { Pentest, Viewer } from '../types';
import { AsyncBoundary, Notice, SeverityBadge, StatusBadge, formatDate, useAsync } from '../ui';

export function PentestsView({ config, viewer }: { config: PortalConfig; viewer: Viewer }) {
  const state = useAsync(() => api.pentests(config), [config]);
  const [expanded, setExpanded] = useState<string | undefined>();
  const [starting, setStarting] = useState<string | undefined>();
  const [message, setMessage] = useState<string | undefined>();

  async function start(pentest: Pentest) {
    setStarting(pentest.pentestId);
    setMessage(undefined);

    try {
      await api.startPentestRun(config, pentest.pentestId);
      setMessage(`Started ${pentest.title}. A full run usually takes a few hours.`);
      state.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not start the run.');
    } finally {
      setStarting(undefined);
    }
  }

  return (
    <>
      <p className="muted">
        Every onboarded app has two configurations: one testing what an anonymous visitor can reach, and one signed in as
        a dedicated Cognito user. Onboard an app on the Apps tab to create a new pair.
      </p>

      {!viewer.isAdmin ? (
        <Notice kind="warn">
          You can read results but not start runs. That needs the <code>{viewer.adminPermission}</code> permission.
        </Notice>
      ) : null}

      {message ? <Notice>{message}</Notice> : null}

      <div className="section">
        <AsyncBoundary state={state}>
          {({ pentests }) =>
            pentests.length === 0 ? (
              <Notice kind="warn">No pen test configurations yet. Onboard an app on the Apps tab to create a pair.</Notice>
            ) : (
              <div className="card">
                <table>
                  <thead>
                    <tr>
                      <th>Application</th>
                      <th>Mode</th>
                      <th>Target</th>
                      <th>Latest run</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {pentests.map((pentest) => (
                      <PentestRow
                        key={pentest.pentestId}
                        config={config}
                        pentest={pentest}
                        expanded={expanded === pentest.pentestId}
                        onToggle={() => setExpanded(expanded === pentest.pentestId ? undefined : pentest.pentestId)}
                        starting={starting === pentest.pentestId}
                        canStart={viewer.isAdmin}
                        onStart={() => start(pentest)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </AsyncBoundary>
      </div>
    </>
  );
}

function PentestRow({
  config,
  pentest,
  expanded,
  onToggle,
  starting,
  canStart,
  onStart,
}: {
  config: PortalConfig;
  pentest: Pentest;
  expanded: boolean;
  onToggle: () => void;
  starting: boolean;
  canStart: boolean;
  onStart: () => void;
}) {
  return (
    <>
      <tr>
        <td>
          <div className="finding-title">{pentest.appName ?? pentest.title}</div>
          {pentest.repository ? <code>{pentest.repository}</code> : null}
        </td>
        <td>
          <span className={`badge ${pentest.mode === 'authenticated' ? 'medium' : 'info'}`}>
            {pentest.mode ?? 'unknown'}
          </span>
        </td>
        <td>
          {pentest.sandboxUrl ? (
            <a href={pentest.sandboxUrl} target="_blank" rel="noreferrer">
              {pentest.sandboxUrl.replace(/^https:\/\//, '')}
            </a>
          ) : (
            <span className="muted">—</span>
          )}
        </td>
        <td>
          {pentest.latestRun ? (
            <>
              <StatusBadge value={pentest.latestRun.status} />
              <div className="muted">{formatDate(pentest.latestRun.startedAt)}</div>
              {pentest.latestRun.reportUrl ? (
                <a href={pentest.latestRun.reportUrl} target="_blank" rel="noreferrer">
                  Report
                </a>
              ) : null}
            </>
          ) : (
            <span className="muted">Never run</span>
          )}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <button className="action secondary" onClick={onToggle} disabled={!pentest.latestRun}>
            {expanded ? 'Hide findings' : 'Findings'}
          </button>{' '}
          <button
            className="action"
            onClick={onStart}
            disabled={!canStart || starting || pentest.latestRun?.status === 'IN_PROGRESS'}
          >
            {starting ? 'Starting…' : 'Run'}
          </button>
        </td>
      </tr>

      {expanded ? (
        <tr>
          <td colSpan={5}>
            <PentestFindings config={config} pentestId={pentest.pentestId} jobId={pentest.latestRun?.jobId} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function PentestFindings({
  config,
  pentestId,
  jobId,
}: {
  config: PortalConfig;
  pentestId: string;
  jobId?: string;
}) {
  const state = useAsync(() => api.pentestFindings(config, pentestId, jobId), [config, pentestId, jobId]);

  return (
    <AsyncBoundary state={state}>
      {({ findings }) =>
        findings.length === 0 ? (
          <p className="muted">No findings from the latest run.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Risk</th>
                <th>Finding</th>
                <th>Type</th>
                <th>Confidence</th>
                <th>Status</th>
                <th>Found</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((finding) => (
                <tr key={finding.id}>
                  <td>
                    <SeverityBadge value={finding.riskLevel} />
                  </td>
                  <td className="finding-title">{finding.name}</td>
                  <td className="muted">{finding.riskType?.replace(/_/g, ' ') ?? '—'}</td>
                  <td className="muted">{finding.confidence ?? '—'}</td>
                  <td>
                    <StatusBadge value={finding.status} />
                  </td>
                  <td className="muted">{formatDate(finding.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </AsyncBoundary>
  );
}
